import {
	type BooleanRuleKey,
	type NumberRuleKey,
	type PowerUpKind,
	RULE_SPECS,
	TICK_MS,
} from "../sim";

/**
 * Player-facing description of the numeric match options and the slider model that edits
 * them. Sliders work in display units (seconds for tick values); nullable options get one
 * extra slider stop at an end labelled e.g. "Unlimited" or "Map". Pure, unit-tested.
 */
export interface NumberOption {
	key: NumberRuleKey;
	label: string;
	hint?: string;
	/** Slider step in display units. */
	uiStep: number;
	/** Label and slider end of the `null` value (nullable options only). */
	nullLabel?: string;
	nullAt?: "min" | "max";
}

export interface OptionSection {
	title: string;
	/** On/off rules shown first in the section. */
	toggles: BooleanRuleKey[];
	/** Sliders, disabled while `requires` (if any) is off. */
	numbers: NumberOption[];
	requires?: BooleanRuleKey;
	/** Per-type power-up switches. */
	powerUpTypes?: boolean;
	/** Starts collapsed behind a "Show" button. */
	advanced?: boolean;
}

export const OPTION_SECTIONS: readonly OptionSection[] = [
	{
		title: "Physics",
		toggles: [
			"wrapPhysics",
			"fuel",
			"damage",
			"laserBounce",
			"asteroidSplitting",
			"safeAsteroidSpawns",
		],
		numbers: [
			{
				key: "planetGrowth",
				label: "Growing planets",
				hint: "Mass a planet gains per asteroid that crashes into it (up to double): gravity grows as the match goes on.",
				uiStep: 5,
				nullLabel: "Off",
				nullAt: "min",
			},
		],
	},
	{
		title: "Fuel",
		toggles: ["fuelStations"],
		requires: "fuel",
		numbers: [
			{
				key: "fuelBurnRate",
				label: "Burn rate",
				hint: "How fast thrusting drains the tank.",
				uiStep: 0.25,
			},
			{
				key: "fuelRegenRate",
				label: "Regen rate",
				hint: "How fast the tank refills while coasting (not thrusting).",
				uiStep: 0.25,
			},
		],
	},
	{
		title: "Laser",
		toggles: ["laserOverheat"],
		requires: "laserOverheat",
		numbers: [
			{ key: "laserHeatMax", label: "Max heat", uiStep: 5 },
			{ key: "laserHeatPerShot", label: "Heat per shot", uiStep: 0.5 },
			{ key: "laserCooldownTicks", label: "Cooldown (max → cold)", uiStep: 0.5 },
		],
	},
	{
		title: "Laser range",
		toggles: [],
		numbers: [
			{
				key: "laserRange",
				label: "Range",
				hint: "How far a laser flies before it fades.",
				uiStep: 100,
				nullLabel: "Classic",
				nullAt: "min",
			},
		],
	},
	{
		title: "Missiles",
		toggles: [],
		numbers: [
			{
				key: "missilesPerLife",
				label: "Missiles per life",
				uiStep: 1,
				nullLabel: "Unlimited",
				nullAt: "max",
			},
			{
				key: "homingTurnDeg",
				label: "Homing turn rate",
				hint: "How sharply homing missiles steer.",
				uiStep: 0.1,
			},
			{ key: "homingLockTicks", label: "Homing lock time", uiStep: 0.5 },
		],
	},
	{
		title: "Match",
		toggles: [],
		numbers: [
			{
				key: "scoreLimit",
				label: "Score limit",
				hint: "Duels: first to this score wins (+1 per kill, −1 per crash), with unlimited ships. Lives = the original 10 ships each.",
				uiStep: 1,
				nullLabel: "Lives",
				nullAt: "min",
			},
		],
	},
	{
		title: "Spawn points",
		toggles: [],
		numbers: [
			{
				key: "spawnPoints",
				label: "Spawn points",
				hint: "The map's own, or 1–10 at random spots.",
				uiStep: 1,
				nullLabel: "Map",
				nullAt: "min",
			},
		],
	},
	{
		title: "Power-ups",
		toggles: ["powerUps"],
		requires: "powerUps",
		powerUpTypes: true,
		numbers: [
			{ key: "powerUpIntervalMinTicks", label: "Spawn every (min)", uiStep: 0.5 },
			{ key: "powerUpIntervalMaxTicks", label: "Spawn every (max)", uiStep: 0.5 },
			{ key: "powerUpMax", label: "Max on the map", uiStep: 1 },
			{ key: "powerUpLifetimeTicks", label: "Pod lifetime", uiStep: 1 },
			{ key: "powerUpDurationTicks", label: "Active duration", uiStep: 0.5 },
		],
	},
	{
		title: "Power-up tuning",
		toggles: [],
		requires: "powerUps",
		advanced: true,
		numbers: [
			{
				key: "tripleLaserDurationTicks",
				label: "Triple laser duration",
				uiStep: 0.5,
				nullLabel: "Same",
				nullAt: "min",
			},
			{ key: "tripleLaserSpreadDeg", label: "Triple laser spread", uiStep: 1 },
			{
				key: "warpDurationTicks",
				label: "Warp duration",
				uiStep: 0.5,
				nullLabel: "Same",
				nullAt: "min",
			},
			{ key: "warpThrustFactor", label: "Warp thrust", uiStep: 0.1 },
			{ key: "warpSpeedFactor", label: "Warp top speed", uiStep: 0.1 },
			{ key: "shieldHits", label: "Shield strength (laser hits)", uiStep: 1 },
			{ key: "freezeTicks", label: "Freeze duration", uiStep: 0.5 },
			{ key: "extraMissiles", label: "Extra missiles", uiStep: 1 },
			{ key: "homingShots", label: "Homing shots", uiStep: 1 },
			{ key: "fuelRefill", label: "Fuel refill", uiStep: 10 },
			{ key: "repairHull", label: "Repair (hull points)", uiStep: 1 },
		],
	},
];

export const POWER_UP_LABELS: Record<PowerUpKind, { label: string; description: string }> = {
	tripleLaser: { label: "Triple laser", description: "Every shot fires three lasers in a fan." },
	warp: { label: "Warp speed", description: "Stronger thrust and a higher top speed." },
	shield: { label: "Shield", description: "Absorbs lasers, a missile, an asteroid or a crash." },
	freeze: { label: "Freeze", description: "An opponent can't steer or shoot for a moment." },
	extraMissiles: { label: "Extra missiles", description: "More missiles (with a missile limit)." },
	homing: { label: "Homing missiles", description: "Your next missiles steer towards enemies." },
	fuel: { label: "Fuel refill", description: "Tops up the tank (with the fuel rule)." },
	repair: { label: "Repair", description: "Restores hull points (with hull damage)." },
};

const secondsPerTick = TICK_MS / 1000;

function toUnits(key: NumberRuleKey, value: number): number {
	return RULE_SPECS[key].unit === "ticks" ? value * secondsPerTick : value;
}

function fromUnits(key: NumberRuleKey, value: number): number {
	return RULE_SPECS[key].unit === "ticks" ? Math.round(value / secondsPerTick) : value;
}

/** Rounds to the slider step without float noise. */
function snap(value: number, step: number): number {
	return Number((Math.round(value / step) * step).toFixed(4));
}

export interface SliderModel {
	min: number;
	max: number;
	step: number;
}

/** Slider range in display units, including the extra stop of a nullable option. */
export function sliderModel(option: NumberOption, maxLaserRange: number): SliderModel {
	const spec = RULE_SPECS[option.key];
	const step = option.uiStep;
	let min = snap(toUnits(option.key, spec.min), step);
	let max = snap(toUnits(option.key, spec.max), step);
	if (option.key === "laserRange") max = Math.min(max, snap(maxLaserRange, step));
	if (option.nullAt === "min") min -= step;
	if (option.nullAt === "max") max += step;
	return { min: Number(min.toFixed(4)), max: Number(max.toFixed(4)), step };
}

/** Slider position of a rule value. */
export function toSlider(option: NumberOption, value: number | null, model: SliderModel): number {
	if (value === null) return option.nullAt === "max" ? model.max : model.min;
	const lo = option.nullAt === "min" ? model.min + model.step : model.min;
	const hi = option.nullAt === "max" ? model.max - model.step : model.max;
	return Math.min(hi, Math.max(lo, snap(toUnits(option.key, value), model.step)));
}

/** Rule value of a slider position (null at the nullable end). */
export function fromSlider(
	option: NumberOption,
	position: number,
	model: SliderModel,
): number | null {
	if (option.nullAt === "min" && position <= model.min) return null;
	if (option.nullAt === "max" && position >= model.max) return null;
	const spec = RULE_SPECS[option.key];
	const value = fromUnits(option.key, snap(position, model.step));
	return Math.min(spec.max, Math.max(spec.min, value));
}

/** The value as players read it. */
export function formatOption(option: NumberOption, value: number | null): string {
	if (value === null) return option.nullLabel ?? "—";
	const v = toUnits(option.key, value);
	switch (RULE_SPECS[option.key].unit) {
		case "ticks":
			return `${trim(v, 1)} s`;
		case "deg":
			return option.key === "homingTurnDeg"
				? `${Math.round(value / secondsPerTick)}°/s`
				: `${trim(v, 0)}°`;
		case "factor":
			return `×${trim(v, 1)}`;
		case "units":
			return option.key === "fuelRefill" ? `+${trim(v, 0)}` : `${trim(v, 0)} u`;
		default:
			return trim(v, 1);
	}
}

function trim(v: number, digits: number): string {
	return String(Number(v.toFixed(digits)));
}
